import { getActiveUploadTaskIds } from './active-uploads-registry';

const CHANNEL_NAME = 'bombiix-uploads';

type CoordinatorMessage =
  | { type: 'active'; taskIds: string[] }
  | { type: 'completed'; taskId: string }
  | { type: 'query' };

const COMPLETED_EVENT = 'upload-completed-elsewhere';

/** Reference callback used only to derive CompletionCallback, so the type carries no unused type-position parameter name. */
const completionCallbackShape = (completedTaskId: string): void => {
  String(completedTaskId);
};
type CompletionCallback = typeof completionCallbackShape;

const foreignLiveTaskIds = new Set<string>();
const completionBus = new EventTarget();

/**
 * The BroadcastChannel this tab uses to coordinate uploads with its sibling tabs, or null when the
 * browser does not support it (older Safari) — in which case cross-tab coordination is a no-op and
 * each tab behaves independently, exactly as before this feature existed.
 */
const channel: BroadcastChannel | null = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;

/**
 * Broadcasts the task ids currently uploading in this tab so other tabs can exclude them from the
 * resumable/failed list. Also answers a newcomer tab's who-is-live query.
 */
function announceActiveUploads(): void {
  if (!channel) return;
  channel.postMessage({ type: 'active', taskIds: getActiveUploadTaskIds() });
}

/**
 * Tells sibling tabs an upload finished, so any stale resumable/failed row they are showing for it
 * can be cleared immediately rather than lingering until a reload.
 * @param taskId - The completed upload's task id.
 */
function announceUploadCompleted(taskId: string): void {
  if (!channel) return;
  channel.postMessage({ type: 'completed', taskId });
}

/**
 * Whether a task id is currently uploading in another tab. A caller uses this to avoid presenting a
 * live upload as a failed/resumable one.
 * @param taskId - Task id to check.
 * @returns True when another tab reports the upload as in-flight.
 */
function isLiveInAnotherTab(taskId: string): boolean {
  return foreignLiveTaskIds.has(taskId);
}

/**
 * Registers a listener fired when a sibling tab reports an upload completed, so the current tab can
 * drop a stale row for that task id.
 * @param listener - Called with the completed task id.
 * @returns An unsubscribe function.
 */
function onUploadCompletedElsewhere(onCompleted: CompletionCallback): () => void {
  const handler = (event: Event): void => {
    const completedTaskId = (event as CustomEvent<string>).detail;
    onCompleted(completedTaskId);
  };
  completionBus.addEventListener(COMPLETED_EVENT, handler);
  return () => {
    completionBus.removeEventListener(COMPLETED_EVENT, handler);
  };
}

/**
 * Asks sibling tabs which uploads they currently have in-flight. Called when a tab mounts, so it
 * learns about uploads that started before it opened.
 */
function requestLiveUploads(): void {
  if (!channel) return;
  channel.postMessage({ type: 'query' });
}

if (channel) {
  channel.onmessage = (event: MessageEvent<CoordinatorMessage>) => {
    const message = event.data;
    if (!message) return;
    if (message.type === 'active') {
      message.taskIds.forEach((taskId) => foreignLiveTaskIds.add(taskId));
      return;
    }
    if (message.type === 'completed') {
      foreignLiveTaskIds.delete(message.taskId);
      completionBus.dispatchEvent(new CustomEvent(COMPLETED_EVENT, { detail: message.taskId }));
      return;
    }
    if (message.type === 'query') {
      announceActiveUploads();
    }
  };
}

export { announceActiveUploads, announceUploadCompleted, isLiveInAnotherTab, onUploadCompletedElsewhere, requestLiveUploads };
